import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  NotificationAddress,
  NotificationEvent,
  NotificationRule,
  NotificationRuleInput,
  TeamMember,
  TeamNotifications,
} from '@/types/team-settings';
import { toError } from '@/utils/errors';

export interface UseTeamNotificationsResult {
  /** The notifications a team can choose recipients for; empty until loaded. */
  events: NotificationEvent[];
  /** The team's rules and copy addresses; null until loaded or on error. */
  notifications: TeamNotifications | null;
  /** The team's client users. Empty if the roster could not be read — see
   *  `membersError`; the rest of the settings still work without it. */
  members: TeamMember[];
  isLoading: boolean;
  /** The settings themselves could not be loaded. */
  error: Error | null;
  /** Only the roster could not be loaded, so choosing people is unavailable. */
  membersError: Error | null;
  refetch: () => Promise<void>;
  /** `ruleKey` is `default` for the team default, else a notification's type. */
  saveRule: (ruleKey: string, input: NotificationRuleInput) => Promise<NotificationRule>;
  removeRule: (ruleKey: string) => Promise<void>;
  registerAddress: (email: string) => Promise<NotificationAddress>;
  resendAddress: (addressId: string) => Promise<NotificationAddress>;
  removeAddress: (addressId: string) => Promise<void>;
}

/**
 * One team's notification settings, for the drawer that edits them.
 *
 * Loads the catalogue, the team's settings and the team roster together, and
 * reloads the settings after every change so the screen shows what the server
 * now holds (address status in particular changes server-side). Actions throw:
 * the caller knows how to present a failure, and several are expected to fail
 * in ways the admin can act on (an address that won't take, a roster that is
 * briefly unavailable).
 *
 * Nothing loads until `enabled`, so a closed drawer costs nothing.
 */
export function useTeamNotifications(params: {
  teamId: string | null;
  enabled: boolean;
}): UseTeamNotificationsResult {
  const { teamId, enabled } = params;
  const active = enabled && teamId !== null;

  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [notifications, setNotifications] = useState<TeamNotifications | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [membersError, setMembersError] = useState<Error | null>(null);

  const load = useCallback(
    async (isCurrent: () => boolean) => {
      if (teamId === null) {
        return;
      }
      setIsLoading(true);
      const [eventsResult, notificationsResult, membersResult] = await Promise.allSettled([
        vnextApiClient.listNotificationEvents(),
        vnextApiClient.getTeamNotifications(teamId),
        vnextApiClient.listTeamMembers(teamId),
      ]);
      if (!isCurrent()) {
        return;
      }

      const settingsFailure =
        eventsResult.status === 'rejected'
          ? eventsResult.reason
          : notificationsResult.status === 'rejected'
            ? notificationsResult.reason
            : null;
      if (settingsFailure !== null) {
        Sentry.captureException(toError(settingsFailure));
        setError(toError(settingsFailure));
        setEvents([]);
        setNotifications(null);
      } else if (
        eventsResult.status === 'fulfilled' &&
        notificationsResult.status === 'fulfilled'
      ) {
        setError(null);
        setEvents(eventsResult.value);
        setNotifications(notificationsResult.value);
      }

      if (membersResult.status === 'fulfilled') {
        setMembers(membersResult.value);
        setMembersError(null);
      } else {
        Sentry.captureException(toError(membersResult.reason));
        setMembers([]);
        setMembersError(toError(membersResult.reason));
      }
      setIsLoading(false);
    },
    [teamId]
  );

  useEffect(() => {
    if (!active) {
      setEvents([]);
      setNotifications(null);
      setMembers([]);
      setError(null);
      setMembersError(null);
      return undefined;
    }
    let cancelled = false;
    void load(() => !cancelled);
    return () => {
      cancelled = true;
    };
  }, [active, load]);

  const refetch = useCallback(async () => {
    if (active) {
      await load(() => true);
    }
  }, [active, load]);

  // After a change, re-read only the settings: the catalogue and roster are
  // unaffected, and re-fetching them would flash the drawer for nothing.
  const reloadSettings = useCallback(async () => {
    if (teamId === null) {
      return;
    }
    setNotifications(await vnextApiClient.getTeamNotifications(teamId));
  }, [teamId]);

  const requireTeam = useCallback((): string => {
    if (teamId === null) {
      throw new Error('No team selected');
    }
    return teamId;
  }, [teamId]);

  const saveRule = useCallback(
    async (ruleKey: string, input: NotificationRuleInput) => {
      const saved = await vnextApiClient.putNotificationRule(requireTeam(), ruleKey, input);
      await reloadSettings();
      return saved;
    },
    [requireTeam, reloadSettings]
  );

  const removeRule = useCallback(
    async (ruleKey: string) => {
      await vnextApiClient.deleteNotificationRule(requireTeam(), ruleKey);
      await reloadSettings();
    },
    [requireTeam, reloadSettings]
  );

  const registerAddress = useCallback(
    async (email: string) => {
      try {
        return await vnextApiClient.registerNotificationAddress(requireTeam(), email);
      } finally {
        // Also on failure: a failed send still leaves the address saved, pending.
        await reloadSettings();
      }
    },
    [requireTeam, reloadSettings]
  );

  const resendAddress = useCallback(
    async (addressId: string) => {
      try {
        return await vnextApiClient.resendNotificationAddress(requireTeam(), addressId);
      } finally {
        await reloadSettings();
      }
    },
    [requireTeam, reloadSettings]
  );

  const removeAddress = useCallback(
    async (addressId: string) => {
      await vnextApiClient.deleteNotificationAddress(requireTeam(), addressId);
      await reloadSettings();
    },
    [requireTeam, reloadSettings]
  );

  return {
    events,
    notifications,
    members,
    isLoading,
    error,
    membersError,
    refetch,
    saveRule,
    removeRule,
    registerAddress,
    resendAddress,
    removeAddress,
  };
}
